import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, LogIn } from "lucide-react";

const STORAGE_KEY = "cashback_demo_seen_v1";
const BONUS_STORAGE_KEY = "cashback_demo_bonus_v1";

// Guest demo cashback: single random amount in £1.00-£2.00 range.
// (Matches the sign-up bonus band the user asked for.)
export function pickDemoOutcome() {
  const min = 1.00, max = 2.00;
  return Math.round((min + Math.random() * (max - min)) * 100) / 100;
}

export function shouldShowDemo() {
  try { return !localStorage.getItem(STORAGE_KEY); } catch { return false; }
}

export function markDemoSeen() {
  try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
}

export function setPendingDemoBonus(amount) {
  try { localStorage.setItem(BONUS_STORAGE_KEY, String(amount)); } catch {}
}

export function getPendingDemoBonus() {
  try {
    const raw = localStorage.getItem(BONUS_STORAGE_KEY);
    if (!raw) return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch { return null; }
}

export function clearPendingDemoBonus() {
  try { localStorage.removeItem(BONUS_STORAGE_KEY); } catch {}
}

/**
 * Intro popup shown on a guest's first visit. Two paths:
 *   - "Start the tour" -> navigates to /upload?demo=1 (Upload page runs in demo mode)
 *   - "Already have an account? Sign in" -> /login
 */
export default function DemoFlow({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;

  const close = (markSeen = true) => {
    if (markSeen) markDemoSeen();
    onClose?.();
  };

  const startTour = () => {
    markDemoSeen();
    onClose?.();
    navigate("/upload?demo=1");
  };

  const goSignIn = () => {
    markDemoSeen();
    onClose?.();
    navigate("/login");
  };

  return (
    <div
      data-testid="demo-flow"
      style={{
        position: "fixed", inset: 0, zIndex: 260,
        background: "rgba(14, 34, 64, 0.86)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        data-testid="demo-intro"
        style={{
          width: "100%", maxWidth: 380,
          background: "var(--surface)", borderRadius: 28,
          padding: "30px 22px 22px", position: "relative", textAlign: "center",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.45)",
        }}
      >
        <div style={{
          width: 80, height: 80, borderRadius: 26, margin: "0 auto 14px",
          background: "var(--primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 18px 32px -16px var(--primary)",
        }}>
          <Sparkles size={36} color="#FFFFFF" />
        </div>
        <h2 style={{
          color: "var(--navy)", fontSize: 22, fontWeight: 800,
          letterSpacing: -0.4, margin: "0 0 8px",
        }}>
          New here? Take a quick demo
        </h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.5, margin: "0 0 18px" }}>
          We'll walk you through uploading a sample receipt.
          Any cash you win in the demo lands in your wallet the moment you create an account.
        </p>

        <button
          data-testid="demo-start-button"
          onClick={startTour}
          className="btn-primary"
          style={{
            width: "100%", display: "inline-flex", alignItems: "center",
            justifyContent: "center", gap: 8,
            padding: "14px 16px",
            fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
          }}
        >
          <Sparkles size={16} />
          Start the demo
          <ChevronRight size={16} />
        </button>
        <button
          data-testid="demo-signin-button"
          onClick={goSignIn}
          style={{
            width: "100%", marginTop: 10,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: "transparent", border: "1px solid var(--border-strong)",
            borderRadius: 999, padding: "12px 16px",
            color: "var(--text-primary)", fontSize: 13, fontWeight: 800, letterSpacing: 0.4,
            cursor: "pointer",
          }}
        >
          <LogIn size={16} />
          Already have an account? Sign in
        </button>
        <button
          data-testid="demo-dismiss-button"
          onClick={() => close(true)}
          style={{
            marginTop: 8, background: "transparent", border: "none",
            color: "var(--text-muted)", fontSize: 12, fontWeight: 700,
            padding: 8, cursor: "pointer",
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
